/* eslint-disable react-refresh/only-export-components */
import {
  faCircleExclamation,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"
import { cn } from "cn"

const inlineAlertVariants = cva(
  "inline-flex items-center gap-1 text-labels-primary [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      size: {
        xs: "text-xs leading-4 font-normal",
        sm: "text-sm leading-5 font-semibold",
      },
      type: {
        error: "[&_svg]:text-error-red-200",
        warning: "[&_svg]:text-accent-orange-100",
      },
    },
    compoundVariants: [
      // Figma exportiert das Warndreieck bei gleicher Höhe geringfügig
      // breiter als das Fehler-Icon (nicht quadratisches Glyph) — hier auf
      // eine einheitliche quadratische Box je Größe normalisiert.
      { size: "xs", class: "[&_svg]:size-3.5" },
      { size: "sm", class: "[&_svg]:size-[18px]" },
    ],
    defaultVariants: {
      size: "xs",
      type: "error",
    },
  }
)

const inlineAlertIconByType = {
  error: faCircleExclamation,
  warning: faTriangleExclamation,
} as const

export type InlineAlertSize = NonNullable<
  VariantProps<typeof inlineAlertVariants>["size"]
>

export type InlineAlertType = NonNullable<
  VariantProps<typeof inlineAlertVariants>["type"]
>

export type InlineAlertProps = ComponentProps<"div"> &
  VariantProps<typeof inlineAlertVariants> & {
    description: string
  }

function InlineAlert({
  className,
  size = "xs",
  type = "error",
  description,
  ...props
}: InlineAlertProps) {
  return (
    <div
      data-slot="inline-alert"
      data-size={size}
      data-type={type}
      className={cn(inlineAlertVariants({ size, type, className }))}
      {...props}
    >
      <FontAwesomeIcon
        data-slot="inline-alert-icon"
        icon={inlineAlertIconByType[type ?? "error"]}
      />
      <p className="min-w-0 flex-1">{description}</p>
    </div>
  )
}

export { InlineAlert, inlineAlertVariants }
